import React, { useEffect, useState } from 'react';
import ConnectingDialog from '../ui/dialog/ConnectingDialog';
import AuthMessageScreen from '../pages/login/AuthMessageScreen';
import {
    fetchTenantConfig,
    loadTenantConfig,
    saveTenantConfig,
    isTenantConfigStale,
    getTenantClientId,
    TENANT_NOT_PROVISIONED,
} from './tenantConfig';
import { getCognitoLoginLang } from '../auth/tokenStore';
import { configureAmplify } from '../auth/amplifyConfig';

// Resolves the tenant (white-label) configuration before rendering the app.
//
// - Returning visitor: children render immediately from the cached config;
//   the config is refreshed in the background when stale (stale-while-
//   revalidate). A changed theme applies on the next full page load, since
//   theme values are read at module-evaluation time (palette.jsx).
// - First visit: one fetch (with retries), then a single reload so the
//   module-scope readers pick up the freshly stored values. This replaces the
//   legacy chain of 2 requests + 2 redirects + 2s of setTimeout.
// - First-visit failure: a retry screen instead of a dead-end dialog.
//
// The tenant's Cognito app client id (client_id, served by the backend with
// the rest of the config) is REQUIRED before the app renders — Amplify cannot
// be configured without it and every login would fail with a silent spinner.
// A cache predating that field (or legacy RO_* keys only) therefore counts as
// "no config": one foreground fetch + reload, the same path as a first visit.
// A tenant the backend serves without a client id gets a specific message
// rather than a network-error one.

// Guards against a reload loop if the freshly saved config does not read
// back as usable (storage quota / privacy mode). One automatic reload max.
const RELOADED_FLAG = 'tenant_bootstrap_reloaded';

const hasUsableConfig = () => !!loadTenantConfig()?.client_id;

const TEXTS = {
    fr: {
        network: {
            title: 'Impossible de charger la configuration de l\'application',
            body: 'Vérifiez votre connexion internet et réessayez.',
            action: 'Réessayer',
        },
        notProvisioned: {
            title: 'Connexion indisponible pour cette organisation',
            body: 'Cette organisation n\'est pas encore activée pour la connexion. Contactez votre administrateur.',
            action: 'Réessayer',
        },
        notPersisted: {
            title: 'Impossible d\'enregistrer la configuration de l\'application',
            body: 'Votre navigateur bloque le stockage local (mode privé ou espace insuffisant). Autorisez-le puis réessayez.',
            action: 'Réessayer',
        },
    },
    en: {
        network: {
            title: 'Unable to load the application configuration',
            body: 'Please check your internet connection and try again.',
            action: 'Retry',
        },
        notProvisioned: {
            title: 'Sign-in is not available for this organization',
            body: 'This organization is not yet enabled for sign-in. Please contact your administrator.',
            action: 'Retry',
        },
        notPersisted: {
            title: 'Unable to save the application configuration',
            body: 'Your browser is blocking local storage (private mode or no space left). Allow it and try again.',
            action: 'Retry',
        },
    },
};

const errorKind = (error) => {
    if (error?.code === TENANT_NOT_PROVISIONED) return 'notProvisioned';
    if (error?.code === 'NOT_PERSISTED') return 'notPersisted';
    return 'network';
};

const TenantBootstrap = ({ children }) => {
    const [state, setState] = useState(() => ({
        status: hasUsableConfig() ? 'ready' : 'loading',
        error: null,
    }));
    const { status, error } = state;

    useEffect(() => {
        let cancelled = false;

        if (status === 'loading') {
            // First visit (or unusable cache): block on the fetch, then reload
            // once so module-scope theme readers see the stored values (deep
            // link preserved).
            fetchTenantConfig()
                .then((config) => {
                    if (cancelled) return;
                    try {
                        saveTenantConfig(config);
                    } catch (storageError) {
                        // localStorage refused the write (quota, privacy mode)
                        const err = new Error('Tenant config not persisted');
                        err.code = 'NOT_PERSISTED';
                        err.cause = storageError;
                        throw err;
                    }
                    if (!hasUsableConfig() || sessionStorage.getItem(RELOADED_FLAG)) {
                        // Either the save did not stick, or we already reloaded
                        // once for this very reason: stop here.
                        const err = new Error('Tenant config not persisted');
                        err.code = 'NOT_PERSISTED';
                        throw err;
                    }
                    sessionStorage.setItem(RELOADED_FLAG, '1');
                    window.location.reload();
                })
                .catch((err) => {
                    console.error('Tenant config fetch failed', err);
                    if (!cancelled) setState({ status: 'error', error: err });
                });
        }

        if (status === 'ready') {
            sessionStorage.removeItem(RELOADED_FLAG);
            // Stale-while-revalidate: never blocks, failures are silent (the
            // cached config keeps working).
            const cached = loadTenantConfig();
            if (!cached || isTenantConfigStale(cached)) {
                fetchTenantConfig()
                    .then((config) => {
                        if (!cancelled) saveTenantConfig(config);
                    })
                    .catch((err) => {
                        console.warn('Tenant config background refresh failed', err);
                    });
            }
        }

        return () => {
            cancelled = true;
        };
    }, [status]);

    if (status === 'error') {
        const texts = (TEXTS[getCognitoLoginLang()] || TEXTS.en)[errorKind(error)];
        return (
            <AuthMessageScreen
                title={texts.title}
                body={texts.body}
                action={texts.action}
                onAction={() => setState({ status: 'loading', error: null })}
            />
        );
    }
    if (status === 'loading') {
        return <ConnectingDialog open={true} />;
    }
    // Amplify needs the tenant's app client id before any auth API call.
    // Guaranteed present here by hasUsableConfig(). Synchronous and
    // idempotent — safe on every render.
    configureAmplify(getTenantClientId());
    return children;
};

export default TenantBootstrap;
